import React, { useState } from 'react';
import {
  Layers,
  BookOpen,
  Plus,
  Edit2,
  Trash2,
  Eye,
  X,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
} from 'lucide-react';
import { AcademicLevel, LevelSyllabusItem, SyllabusLearningType, AdminRole } from '../types';
import { Badge, Button, Input, Search, Tooltip } from '../../components/core';

interface LevelSyllabusManagementProps {
  levels: AcademicLevel[];
  currentRole: AdminRole;
  onAddLevel: (lvl: AcademicLevel) => void;
  onUpdateLevel: (lvl: AcademicLevel) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const getLearningTypeBadgeVariant = (
  type: SyllabusLearningType
): 'soft' | 'accent' | 'warning' | 'success' | 'outline' => {
  switch (type) {
    case 'Sutra':
      return 'soft';
    case 'Stavan':
      return 'accent';
    case 'Story':
      return 'warning';
    case 'Activity':
      return 'success';
    default:
      return 'outline';
  }
};

export const LevelSyllabusManagement: React.FC<LevelSyllabusManagementProps> = ({
  levels,
  currentRole,
  onAddLevel,
  onUpdateLevel,
  addToast,
}) => {
  const isAuditor = currentRole === 'Auditor';

  const [selectedLevelForSyllabus, setSelectedLevelForSyllabus] = useState<AcademicLevel | null>(null);
  const [syllabusSearch, setSyllabusSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');

  const [viewingLevelModal, setViewingLevelModal] = useState<AcademicLevel | null>(null);
  const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<AcademicLevel | null>(null);
  const [levelForm, setLevelForm] = useState({
    code: '',
    name: '',
    description: '',
    minAge: 5,
    maxAge: 10,
    prerequisite: 'None',
  });

  const [isSyllabusModalOpen, setIsSyllabusModalOpen] = useState(false);
  const [editingSyllabusItem, setEditingSyllabusItem] = useState<LevelSyllabusItem | null>(null);
  const [syllabusForm, setSyllabusForm] = useState({
    chapterName: '',
    description: '',
    learningType: 'Sutra' as SyllabusLearningType,
    displayOrder: 1,
    status: 'Active' as 'Active' | 'Inactive',
  });

  const openAddLevel = () => {
    setEditingLevel(null);
    setLevelForm({
      code: `LVL-${levels.length + 1}`,
      name: `Level ${levels.length + 1}`,
      description: '',
      minAge: 5,
      maxAge: 12,
      prerequisite: 'None',
    });
    setIsLevelModalOpen(true);
  };

  const openEditLevel = (lvl: AcademicLevel) => {
    if (isAuditor) return;
    setEditingLevel(lvl);
    setLevelForm({
      code: lvl.code,
      name: lvl.name,
      description: lvl.description,
      minAge: lvl.minAge,
      maxAge: lvl.maxAge,
      prerequisite: lvl.prerequisite,
    });
    setIsLevelModalOpen(true);
  };

  const handleLevelFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    if (editingLevel) {
      const updated: AcademicLevel = {
        ...editingLevel,
        ...levelForm,
      };
      onUpdateLevel(updated);
      if (selectedLevelForSyllabus?.id === editingLevel.id) {
        setSelectedLevelForSyllabus(updated);
      }
      addToast('success', 'Level Updated', `Updated details for ${levelForm.name}`);
    } else {
      const newLevel: AcademicLevel = {
        id: `LVL-${Math.floor(100 + Math.random() * 900)}`,
        ...levelForm,
        enrolledCount: 0,
        syllabus: [],
      };
      onAddLevel(newLevel);
      addToast('success', 'Level Created', `Created ${newLevel.name}`);
    }
    setIsLevelModalOpen(false);
  };

  const activeSyllabus = selectedLevelForSyllabus?.syllabus || [];

  const filteredSyllabusItems = activeSyllabus.filter((item) => {
    const matchesSearch =
      item.chapterName.toLowerCase().includes(syllabusSearch.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(syllabusSearch.toLowerCase()));
    const matchesType = typeFilter === 'ALL' || item.learningType === typeFilter;
    return matchesSearch && matchesType;
  });

  const openAddSyllabusItem = () => {
    setEditingSyllabusItem(null);
    const nextOrder = activeSyllabus.length + 1;
    setSyllabusForm({
      chapterName: '',
      description: '',
      learningType: 'Sutra',
      displayOrder: nextOrder,
      status: 'Active',
    });
    setIsSyllabusModalOpen(true);
  };

  const openEditSyllabusItem = (item: LevelSyllabusItem) => {
    if (isAuditor) return;
    setEditingSyllabusItem(item);
    setSyllabusForm({
      chapterName: item.chapterName,
      description: item.description || '',
      learningType: item.learningType,
      displayOrder: item.displayOrder,
      status: item.status,
    });
    setIsSyllabusModalOpen(true);
  };

  const handleSyllabusFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLevelForSyllabus || isAuditor) return;

    const currentSyllabus = [...(selectedLevelForSyllabus.syllabus || [])];

    if (editingSyllabusItem) {
      const updatedSyllabus = currentSyllabus.map((item) =>
        item.id === editingSyllabusItem.id
          ? {
              ...item,
              chapterName: syllabusForm.chapterName,
              description: syllabusForm.description,
              learningType: syllabusForm.learningType,
              displayOrder: syllabusForm.displayOrder,
              status: syllabusForm.status,
            }
          : item
      );
      updatedSyllabus.sort((a, b) => a.displayOrder - b.displayOrder);

      const updatedLevel: AcademicLevel = {
        ...selectedLevelForSyllabus,
        syllabus: updatedSyllabus,
      };
      setSelectedLevelForSyllabus(updatedLevel);
      onUpdateLevel(updatedLevel);
      addToast('success', 'Syllabus Updated', `Updated "${syllabusForm.chapterName}"`);
    } else {
      const newItem: LevelSyllabusItem = {
        id: `SYL-${Date.now()}`,
        levelId: selectedLevelForSyllabus.id,
        chapterName: syllabusForm.chapterName,
        description: syllabusForm.description,
        learningType: syllabusForm.learningType,
        displayOrder: syllabusForm.displayOrder,
        status: syllabusForm.status,
      };
      const updatedSyllabus = [...currentSyllabus, newItem];
      updatedSyllabus.sort((a, b) => a.displayOrder - b.displayOrder);

      const updatedLevel: AcademicLevel = {
        ...selectedLevelForSyllabus,
        syllabus: updatedSyllabus,
      };
      setSelectedLevelForSyllabus(updatedLevel);
      onUpdateLevel(updatedLevel);
      addToast('success', 'Topic Added', `Added "${newItem.chapterName}" to ${selectedLevelForSyllabus.name}`);
    }

    setIsSyllabusModalOpen(false);
  };

  const handleDeleteSyllabusItem = (itemId: string) => {
    if (!selectedLevelForSyllabus || isAuditor) return;

    const updatedSyllabus = (selectedLevelForSyllabus.syllabus || [])
      .filter((item) => item.id !== itemId)
      .map((item, idx) => ({ ...item, displayOrder: idx + 1 }));

    const updatedLevel: AcademicLevel = {
      ...selectedLevelForSyllabus,
      syllabus: updatedSyllabus,
    };

    setSelectedLevelForSyllabus(updatedLevel);
    onUpdateLevel(updatedLevel);
    addToast('success', 'Item Deleted', 'Syllabus item removed from level.');
  };

  const handleToggleSyllabusStatus = (item: LevelSyllabusItem) => {
    if (!selectedLevelForSyllabus || isAuditor) return;

    const newStatus = item.status === 'Active' ? 'Inactive' : 'Active';
    const updatedSyllabus = (selectedLevelForSyllabus.syllabus || []).map((s) =>
      s.id === item.id ? { ...s, status: newStatus as 'Active' | 'Inactive' } : s
    );

    const updatedLevel: AcademicLevel = {
      ...selectedLevelForSyllabus,
      syllabus: updatedSyllabus,
    };

    setSelectedLevelForSyllabus(updatedLevel);
    onUpdateLevel(updatedLevel);
    addToast('info', 'Status Changed', `Topic "${item.chapterName}" is now ${newStatus}`);
  };

  const handleMoveSyllabusOrder = (index: number, direction: 'up' | 'down') => {
    if (!selectedLevelForSyllabus || isAuditor) return;

    const currentSyllabus = [...(selectedLevelForSyllabus.syllabus || [])];
    const targetIdx = direction === 'up' ? index - 1 : index + 1;

    if (targetIdx < 0 || targetIdx >= currentSyllabus.length) return;

    const temp = currentSyllabus[index];
    currentSyllabus[index] = currentSyllabus[targetIdx];
    currentSyllabus[targetIdx] = temp;

    const reordered = currentSyllabus.map((s, i) => ({
      ...s,
      displayOrder: i + 1,
    }));

    const updatedLevel: AcademicLevel = {
      ...selectedLevelForSyllabus,
      syllabus: reordered,
    };

    setSelectedLevelForSyllabus(updatedLevel);
    onUpdateLevel(updatedLevel);
    addToast('success', 'Order Changed', `Updated syllabus sequence for ${selectedLevelForSyllabus.name}`);
  };

  if (selectedLevelForSyllabus) {
    return (
      <div className="space-y-6">
        <div className="admin-card p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={ArrowLeft}
              onClick={() => setSelectedLevelForSyllabus(null)}
            >
              Back to All Levels
            </Button>

            <div>
              <div className="flex items-center gap-2">
                <Badge variant="default" className="font-mono normal-case">
                  {selectedLevelForSyllabus.code}
                </Badge>
                <h1 className="text-xl font-bold text-[var(--gv-ink)] tracking-tight">
                  Manage Syllabus: {selectedLevelForSyllabus.name}
                </h1>
              </div>
              <p className="text-xs text-[var(--gv-ink-muted)] mt-0.5">
                Configure syllabus topics, order sequence, and learning types for this specific level.
              </p>
            </div>
          </div>

          {!isAuditor && (
            <Button leftIcon={Plus} onClick={openAddSyllabusItem} className="shrink-0">
              Add Syllabus Item
            </Button>
          )}
        </div>

        <div className="bg-[var(--gv-surface)] p-5 rounded-2xl border border-[var(--gv-border)] space-y-3">
          <span className="text-[10px] font-mono font-bold text-[var(--gv-brand)] uppercase tracking-wider block">
            Level Information Summary
          </span>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <span className="text-[var(--gv-ink-muted)] block text-[11px]">Level Code:</span>
              <strong className="text-[var(--gv-ink)] font-mono font-bold">{selectedLevelForSyllabus.code}</strong>
            </div>
            <div>
              <span className="text-[var(--gv-ink-muted)] block text-[11px]">Age Bracket:</span>
              <strong className="text-[var(--gv-ink)] font-bold">{selectedLevelForSyllabus.minAge} - {selectedLevelForSyllabus.maxAge} Years</strong>
            </div>
            <div>
              <span className="text-[var(--gv-ink-muted)] block text-[11px]">Enrolled Students:</span>
              <strong className="text-[var(--gv-ink)] font-bold">{selectedLevelForSyllabus.enrolledCount} Students</strong>
            </div>
            <div>
              <span className="text-[var(--gv-ink-muted)] block text-[11px]">Prerequisite:</span>
              <strong className="text-[var(--gv-ink)] font-bold">{selectedLevelForSyllabus.prerequisite}</strong>
            </div>
          </div>

          {selectedLevelForSyllabus.description && (
            <div className="pt-2 border-t border-[var(--gv-border)] text-xs text-[var(--gv-ink-muted)] leading-relaxed">
              <strong>Description:</strong> {selectedLevelForSyllabus.description}
            </div>
          )}
        </div>

        <div className="admin-card p-5 space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pb-3 border-b border-[var(--gv-border)]">
            <div>
              <h2 className="text-sm font-bold text-[var(--gv-ink)]">Assigned Syllabus Topics ({activeSyllabus.length})</h2>
              <p className="text-xs text-[var(--gv-ink-muted)]">Each Level has its own isolated syllabus structure.</p>
            </div>

            <div className="flex items-center gap-2.5 w-full sm:w-auto">
              <Search
                value={syllabusSearch}
                onChange={setSyllabusSearch}
                placeholder="Search topic or description..."
                containerClassName="flex-1 sm:w-60"
              />

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="admin-input bg-[var(--gv-surface)] border border-[var(--gv-border)] rounded-xl px-3 py-1.5 text-xs font-semibold text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
              >
                <option value="ALL">All Types</option>
                <option value="Sutra">Sutra</option>
                <option value="Stavan">Stavan</option>
                <option value="Story">Story</option>
                <option value="Activity">Activity</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {filteredSyllabusItems.length === 0 ? (
            <div className="text-center py-12 bg-[var(--gv-surface)] rounded-xl border border-dashed border-[var(--gv-border)] space-y-2">
              <BookOpen className="w-8 h-8 text-[var(--gv-ink-muted)] mx-auto" />
              <p className="text-sm font-bold text-[var(--gv-ink)]">No syllabus items found</p>
              <p className="text-xs text-[var(--gv-ink-muted)]">
                {activeSyllabus.length === 0
                  ? 'No syllabus topics have been assigned to this level yet.'
                  : 'No topics match your current search/type filter.'}
              </p>
              {!isAuditor && (
                <Button leftIcon={Plus} onClick={openAddSyllabusItem} className="mt-2">
                  Add First Topic
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="divide-y divide-stone-100 bg-white rounded-xl border border-[var(--gv-border)] shadow-2xs overflow-hidden">
                {filteredSyllabusItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-[var(--gv-surface)]/80 transition-colors"
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <span className="w-8 h-8 rounded-xl bg-[var(--gv-surface)] border border-[var(--gv-border)] text-[var(--gv-brand)] font-mono text-xs font-bold flex items-center justify-center shrink-0">
                        #{item.displayOrder}
                      </span>

                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-xs font-bold text-[var(--gv-ink)]">{item.chapterName}</h3>
                          <Badge variant={getLearningTypeBadgeVariant(item.learningType)} className="font-mono normal-case">
                            {item.learningType}
                          </Badge>
                          <Badge variant={item.status === 'Active' ? 'success' : 'outline'} className="font-mono normal-case">
                            {item.status}
                          </Badge>
                        </div>

                        {item.description && (
                          <p className="text-xs text-[var(--gv-ink-muted)] line-clamp-2 leading-relaxed">{item.description}</p>
                        )}
                      </div>
                    </div>

                    {!isAuditor && (
                      <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                        <Tooltip content="Move Up">
                          <Button
                            variant="outline"
                            size="icon"
                            leftIcon={ArrowUp}
                            disabled={index === 0}
                            aria-label="Move Up"
                            onClick={() => handleMoveSyllabusOrder(index, 'up')}
                          />
                        </Tooltip>

                        <Tooltip content="Move Down">
                          <Button
                            variant="outline"
                            size="icon"
                            leftIcon={ArrowDown}
                            disabled={index === filteredSyllabusItems.length - 1}
                            aria-label="Move Down"
                            onClick={() => handleMoveSyllabusOrder(index, 'down')}
                          />
                        </Tooltip>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleSyllabusStatus(item)}
                          className={
                            item.status === 'Active'
                              ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
                              : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                          }
                        >
                          {item.status === 'Active' ? 'Deactivate' : 'Activate'}
                        </Button>

                        <Button variant="outline" size="sm" leftIcon={Edit2} onClick={() => openEditSyllabusItem(item)}>
                          Edit
                        </Button>

                        <Tooltip content="Delete Topic">
                          <Button
                            variant="outline"
                            size="icon"
                            leftIcon={Trash2}
                            aria-label="Delete Topic"
                            className="border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                            onClick={() => handleDeleteSyllabusItem(item.id)}
                          />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {isSyllabusModalOpen && (
          <div className="fixed inset-0 z-[9990] bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="admin-card max-w-lg w-full p-6 shadow-2xl space-y-4 text-[var(--gv-ink)]">
              <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
                <h3 className="text-base font-bold text-[var(--gv-ink)]">
                  {editingSyllabusItem ? 'Edit Syllabus Topic' : 'Add New Syllabus Topic'}
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  leftIcon={X}
                  aria-label="Close"
                  onClick={() => setIsSyllabusModalOpen(false)}
                />
              </div>

              <form onSubmit={handleSyllabusFormSubmit} className="space-y-3.5 text-xs">
                <Input
                  label="Topic Name *"
                  required
                  value={syllabusForm.chapterName}
                  onChange={(e) => setSyllabusForm({ ...syllabusForm, chapterName: e.target.value })}
                  placeholder="e.g., Navkar Mantra - Verse 1 & Pronunciation"
                />

                <div className="grid grid-cols-2 gap-3">
                  <div className="w-full space-y-1.5">
                    <label className="block text-xs font-bold text-[var(--gv-ink)]">Learning Type</label>
                    <select
                      value={syllabusForm.learningType}
                      onChange={(e) =>
                        setSyllabusForm({ ...syllabusForm, learningType: e.target.value as SyllabusLearningType })
                      }
                      className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                    >
                      <option value="Sutra">Sutra</option>
                      <option value="Stavan">Stavan</option>
                      <option value="Story">Story</option>
                      <option value="Activity">Activity</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <Input
                    label="Display Order"
                    type="number"
                    min={1}
                    required
                    value={syllabusForm.displayOrder}
                    onChange={(e) =>
                      setSyllabusForm({
                        ...syllabusForm,
                        displayOrder: Math.max(1, parseInt(e.target.value) || 1),
                      })
                    }
                  />
                </div>

                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Status</label>
                  <select
                    value={syllabusForm.status}
                    onChange={(e) =>
                      setSyllabusForm({ ...syllabusForm, status: e.target.value as 'Active' | 'Inactive' })
                    }
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Description (Optional)</label>
                  <textarea
                    rows={3}
                    value={syllabusForm.description}
                    onChange={(e) => setSyllabusForm({ ...syllabusForm, description: e.target.value })}
                    placeholder="Brief description of verses, learning outcomes, or chapters..."
                    className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-3 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  />
                </div>

                <div className="pt-3 flex justify-end gap-2 border-t border-[var(--gv-border)]">
                  <Button type="button" variant="secondary" onClick={() => setIsSyllabusModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Save Topic</Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="admin-card p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--gv-ink)] tracking-tight">Level & Syllabus Management</h1>
        </div>

        {!isAuditor && (
          <Button leftIcon={Plus} onClick={openAddLevel} className="shrink-0">
            Create New Level
          </Button>
        )}
      </div>

      <div className="admin-card overflow-hidden">
        <div className="p-4 border-b border-[var(--gv-border)] flex items-center justify-between bg-[var(--gv-surface)]/50">
          <span className="text-xs font-bold text-[var(--gv-ink)] font-mono uppercase tracking-wider">
            Pathshala Levels ({levels.length})
          </span>
          <span className="text-[11px] text-[var(--gv-ink-muted)] font-medium">
            Select "Manage Syllabus" on any level to view & edit its topics
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead className="bg-stone-100/80 text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase tracking-wider border-b border-[var(--gv-border)]">
              <tr>
                <th className="py-3 px-4 font-bold">Level Name</th>
                <th className="py-3 px-4 font-bold">Age Group</th>
                <th className="py-3 px-4 font-bold">Prerequisite</th>
                <th className="py-3 px-4 font-bold">Assigned Topics</th>
                <th className="py-3 px-4 font-bold">Status</th>
                <th className="py-3 px-4 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 font-medium text-[var(--gv-ink)] bg-white">
              {levels.map((lvl) => {
                const syllabusCount = lvl.syllabus?.length || 0;

                return (
                  <tr key={lvl.id} className="hover:bg-[var(--gv-surface)]/80 transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="soft" className="font-mono normal-case">
                          {lvl.code}
                        </Badge>
                        <div>
                          <div className="font-bold text-[var(--gv-ink)] text-xs">{lvl.name}</div>
                          <span className="text-[10px] text-[var(--gv-ink-muted)] line-clamp-1">{lvl.description}</span>
                        </div>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 font-mono font-semibold text-[var(--gv-ink-muted)]">
                      {lvl.minAge} - {lvl.maxAge} Yrs
                    </td>

                    <td className="py-3.5 px-4 font-semibold text-[var(--gv-ink-muted)]">
                      {lvl.prerequisite}
                    </td>

                    <td className="py-3.5 px-4">
                      <Badge variant="warning" className="normal-case">
                        {syllabusCount} Topics
                      </Badge>
                    </td>

                    <td className="py-3.5 px-4">
                      <Badge variant="success" className="font-mono normal-case">
                        Active ({lvl.enrolledCount} Enrolled)
                      </Badge>
                    </td>

                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button variant="secondary" size="sm" leftIcon={Eye} onClick={() => setViewingLevelModal(lvl)}>
                          View
                        </Button>

                        {!isAuditor && (
                          <Button variant="secondary" size="sm" leftIcon={Edit2} onClick={() => openEditLevel(lvl)}>
                            Edit
                          </Button>
                        )}

                        <Button size="sm" leftIcon={BookOpen} onClick={() => setSelectedLevelForSyllabus(lvl)}>
                          Manage Syllabus
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {viewingLevelModal && (
        <div className="fixed inset-0 z-[9990] bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="admin-card max-w-lg w-full p-6 shadow-2xl space-y-5 text-[var(--gv-ink)]">
            <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
              <div className="flex items-center gap-2">
                <Badge variant="soft" className="font-mono normal-case">
                  {viewingLevelModal.code}
                </Badge>
                <h3 className="text-base font-bold text-[var(--gv-ink)]">{viewingLevelModal.name}</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setViewingLevelModal(null)}
              />
            </div>

            <div className="bg-[var(--gv-surface)] p-4 rounded-xl border border-[var(--gv-border)] space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[var(--gv-ink-muted)] block text-[11px] font-mono uppercase">Enrolled Students</span>
                  <strong className="text-[var(--gv-ink)] font-bold text-sm">{viewingLevelModal.enrolledCount} Students</strong>
                </div>
                <div>
                  <span className="text-[var(--gv-ink-muted)] block text-[11px] font-mono uppercase">Age Bracket</span>
                  <strong className="text-[var(--gv-ink)] font-bold text-sm">{viewingLevelModal.minAge} - {viewingLevelModal.maxAge} Years</strong>
                </div>
                <div>
                  <span className="text-[var(--gv-ink-muted)] block text-[11px] font-mono uppercase">Prerequisite</span>
                  <strong className="text-[var(--gv-ink)] font-bold">{viewingLevelModal.prerequisite}</strong>
                </div>
                <div>
                  <span className="text-[var(--gv-ink-muted)] block text-[11px] font-mono uppercase">Syllabus Topics</span>
                  <strong className="text-[var(--gv-brand)] font-bold">{viewingLevelModal.syllabus?.length || 0} Assigned</strong>
                </div>
              </div>

              <div>
                <span className="text-[var(--gv-ink-muted)] block text-[11px] font-mono uppercase mb-1">Description</span>
                <p className="bg-white p-3 rounded-lg border border-[var(--gv-border)] text-[var(--gv-ink)] leading-relaxed">
                  {viewingLevelModal.description}
                </p>
              </div>
            </div>

            <div className="pt-2 flex justify-between items-center">
              <Button
                leftIcon={BookOpen}
                onClick={() => {
                  const target = viewingLevelModal;
                  setViewingLevelModal(null);
                  setSelectedLevelForSyllabus(target);
                }}
              >
                Open Syllabus Manager
              </Button>

              <Button variant="secondary" onClick={() => setViewingLevelModal(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLevelModalOpen && (
        <div className="fixed inset-0 z-[9990] bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="admin-card max-w-md w-full p-6 shadow-2xl space-y-4 text-[var(--gv-ink)]">
            <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">
                {editingLevel ? `Edit Level: ${editingLevel.code}` : 'Create New Pathshala Level'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsLevelModalOpen(false)}
              />
            </div>

            <form onSubmit={handleLevelFormSubmit} className="space-y-3.5 text-xs">
              <Input
                label="Level Code *"
                required
                className="font-mono font-bold"
                value={levelForm.code}
                onChange={(e) => setLevelForm({ ...levelForm, code: e.target.value })}
                placeholder="e.g. LVL-01"
              />

              <Input
                label="Level Name *"
                required
                className="font-bold"
                value={levelForm.name}
                onChange={(e) => setLevelForm({ ...levelForm, name: e.target.value })}
                placeholder="e.g. Level 1 - Prarambhik"
              />

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Description</label>
                <textarea
                  rows={2}
                  required
                  value={levelForm.description}
                  onChange={(e) => setLevelForm({ ...levelForm, description: e.target.value })}
                  placeholder="Overview of level objectives..."
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-3 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Min Age"
                  type="number"
                  required
                  className="font-bold"
                  value={levelForm.minAge}
                  onChange={(e) => setLevelForm({ ...levelForm, minAge: Number(e.target.value) })}
                />
                <Input
                  label="Max Age"
                  type="number"
                  required
                  className="font-bold"
                  value={levelForm.maxAge}
                  onChange={(e) => setLevelForm({ ...levelForm, maxAge: Number(e.target.value) })}
                />
              </div>

              <div className="pt-3 flex justify-end gap-2 border-t border-[var(--gv-border)]">
                <Button type="button" variant="secondary" onClick={() => setIsLevelModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Level</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
